import { computed, ref, watch } from 'vue';
import { defineStore } from 'pinia';
import { useTitle, useWindowSize } from '@vueuse/core';

import { i18n } from '@apps/main/modules/localization/localizationSetup';

import type { Coordinate } from '@libs/trip-planner-interfaces';
import { Breakpoints, isLargerThan, isSmallerThan } from '@libs/ui';

import type { SessionStorageKey } from '../models';

import { useRecipeStore } from './recipe/recipeStore';

const { t } = i18n.global;

// NOTE:
// Purpose of this file is to hold utilities related to browser capabilities and nothing else.

export const useBrowserStore = defineStore('BrowserStore', () => {
  // State
  const geolocationApiCoordinates = ref<Coordinate | undefined>(undefined);
  const recipeStore = useRecipeStore();
  const premadeTripTitle = ref<string | undefined>(undefined);
  const { width } = useWindowSize();

  // Internal state
  let previousTitle = '';
  const hotelsDisplayTexts = computed(() =>
    recipeStore.hotelOnlyRecipe.map((hotel) => hotel.displayText)
  );

  // NOTE: premade trip title has always priority over hotel ingredients
  watch(
    [hotelsDisplayTexts, premadeTripTitle],
    ([displayText, premadeTripTitle]) => {
      if (premadeTripTitle) {
        if (previousTitle !== premadeTripTitle) {
          useTitle(premadeTripTitle);
          previousTitle = premadeTripTitle;
        }
        return;
      }

      if (displayText && displayText.length > 0) {
        const title = t('planner.page_title');
        const locations = displayText.join(' → ');

        const newTitle = `${title} ${locations}`;
        if (previousTitle !== newTitle) {
          useTitle(newTitle);
          previousTitle = newTitle;
        }

        return;
      }
    },
    { immediate: true }
  );

  // Actions
  function loadUserCoordinatesFromGeolocationAPI(): Promise<
    Coordinate | undefined
  > {
    return new Promise<Coordinate | undefined>((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (position: GeolocationPosition) => {
          const geolocationPosition: Coordinate = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          };

          resolve(geolocationPosition);
        },
        () => resolve(undefined)
      );
    });
  }

  function setSessionStorageItem(key: SessionStorageKey, value: string): void {
    sessionStorage.setItem(key, value);
  }

  function getSessionStorageItem(key: SessionStorageKey): string | null {
    return sessionStorage.getItem(key);
  }

  function setPremadeTripTitle(title: string): void {
    premadeTripTitle.value = title;
  }

  // Getters
  const isLargerThan599px = computed(() =>
    isLargerThan(width.value, Breakpoints.Breakpoint599px)
  );

  const isSmallerThan599px = computed(() =>
    isSmallerThan(width.value, Breakpoints.Breakpoint599px)
  );

  const isSmallerThan600px = computed(() =>
    isSmallerThan(width.value, Breakpoints.Breakpoint600px)
  );

  const isLargerThan600px = computed(() =>
    isLargerThan(width.value, Breakpoints.Breakpoint600px)
  );

  const isSmallerThan899px = computed(() =>
    isSmallerThan(width.value, Breakpoints.Breakpoint899px)
  );

  const isLargerThan899px = computed(() =>
    isLargerThan(width.value, Breakpoints.Breakpoint899px)
  );

  const isSmallerThan900px = computed(() =>
    isSmallerThan(width.value, Breakpoints.Breakpoint900px)
  );

  const isLargerThan900px = computed(() =>
    isLargerThan(width.value, Breakpoints.Breakpoint900px)
  );

  const isSmallerThan1199px = computed(() =>
    isSmallerThan(width.value, Breakpoints.Breakpoint1199px)
  );

  const isLargerThan1199px = computed(() =>
    isLargerThan(width.value, Breakpoints.Breakpoint1199px)
  );

  const isSmallerThan1200px = computed(() =>
    isSmallerThan(width.value, Breakpoints.Breakpoint1200px)
  );

  const isLargerThan1200px = computed(() =>
    isLargerThan(width.value, Breakpoints.Breakpoint1200px)
  );

  const isSmallerThan1535px = computed(() =>
    isSmallerThan(width.value, Breakpoints.Breakpoint1535px)
  );

  const isLargerThan1535px = computed(() =>
    isLargerThan(width.value, Breakpoints.Breakpoint1535px)
  );

  const isSmallerThan1536px = computed(() =>
    isSmallerThan(width.value, Breakpoints.Breakpoint1536px)
  );

  const isLargerThan1536px = computed(() =>
    isLargerThan(width.value, Breakpoints.Breakpoint1536px)
  );

  const isSmallerThan2048px = computed(() =>
    isSmallerThan(width.value, Breakpoints.Breakpoint2048px)
  );

  const isLargerThan2048px = computed(() =>
    isLargerThan(width.value, Breakpoints.Breakpoint2048px)
  );

  const isSmallerThan2049px = computed(() =>
    isSmallerThan(width.value, Breakpoints.Breakpoint2049px)
  );

  const isLargerThan2049px = computed(() =>
    isLargerThan(width.value, Breakpoints.Breakpoint2049px)
  );

  return {
    // State
    geolocationApiCoordinates,

    // Actions
    loadUserCoordinatesFromGeolocationAPI,
    setSessionStorageItem,
    getSessionStorageItem,
    setPremadeTripTitle,

    // Getters
    isSmallerThan599px,
    isLargerThan599px,
    isSmallerThan600px,
    isLargerThan600px,
    isSmallerThan899px,
    isLargerThan899px,
    isSmallerThan900px,
    isLargerThan900px,
    isSmallerThan1199px,
    isLargerThan1199px,
    isSmallerThan1200px,
    isLargerThan1200px,
    isSmallerThan1535px,
    isLargerThan1535px,
    isSmallerThan1536px,
    isLargerThan1536px,
    isSmallerThan2048px,
    isLargerThan2048px,
    isSmallerThan2049px,
    isLargerThan2049px,
  };
});
